'use client'

import * as Ariakit from '@ariakit/react'
import { ComponentProps, forwardRef } from 'react'
import styled from 'styled-components'

// eslint-disable-next-line rulesdir/require-next-modal-react-modal-wrapper
import {
  Button as LMFButton,
  Dialog as LMFDialog,
  TypeStyle,
  fromMd,
} from '@syconium/little-miss-figgy'

import {
  NextDialog,
  NextDialogDismiss,
  NextDialogHeader,
  NextDialogProps,
  NextDialogTitle,
} from '../NextDialog.client'

const DialogContent = styled(LMFDialog.Content)`
  height: 100%; // This not a good idea, so I removed it from Dialog.Content, but moving it here to maintian status quo
  padding: 0;
`

/**
 * `InformationalDialog` does not leverage the `header` or `footer` props that
 * are now enforced by `NextDialog`, and the relevant layout elements should be
 * rendered in the body directly. To continue to support existing usage of this
 * component and avoid incorrect usage, we omit those props from being able to
 * be defined on `InformationalDialog.Dialog`.
 */
type InformationalDialogProps = Omit<NextDialogProps, 'header' | 'footer' | 'content'>

/** Top-level dialog wrapper for the informational dialog. */
export const Dialog = styled(NextDialog).attrs({
  content: <DialogContent />,
  unmountOnHide: true,
})<InformationalDialogProps>`
  container-type: inline-size;
  width: fill-available;
  ${fromMd} {
    max-width: 620px;
  }
`

/** The body content of the informational dialog. */
export const Body = styled.div`
  padding: ${o => o.theme.spacing(5)};
  text-align: left;
`
/** The controls container for the informational dialog. Should container the CTA's and actions that a user can take for the dialog subject. */
export const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${o => o.theme.spacing(3)};
  padding: ${o => o.theme.spacing(5)};
  box-sizing: border-box;
  border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  flex-direction: column;
  @container (min-width: 425px) {
    flex-direction: row;
  }
`

/** The header container for the informational dialog. */
export const Header = styled(NextDialogHeader)`
  display: flex;
  align-items: center;
  margin: 0 auto;
  & > :nth-child(2) {
    margin: 0;
  }
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  padding: ${o => o.theme.spacing(3, 5)};
`

const StyledNextDialogTitle = styled(NextDialogTitle)`
  text-align: left;
`

/** The title for the informational dialog. Should be rendered as a direct descendant of `InformationalDialog.Header`. */
export function Title({ children, ...rest }: ComponentProps<typeof StyledNextDialogTitle>) {
  return (
    <StyledNextDialogTitle render={<TypeStyle.HeadlineExtraExtraSmall />} {...rest}>
      {children}
    </StyledNextDialogTitle>
  )
}

/** The description/body text that is displayed within the informational dialog. Should be rendered as a direct descendant of `InformationalDialog.Body`. */
export const Description = styled(TypeStyle.BodyMedium).attrs({ fontWeight: 400 })``

/** A dismiss button to display within the controls for the informational dialog. Will automatically close the dialog when clicked. */
export const DismissButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof NextDialogDismiss>
>(function DismissButton({ children, ...rest }, ref) {
  return (
    <Ariakit.DialogDismiss
      ref={ref}
      render={<LMFButton type='button' size='large' variant='secondary' />}
      {...rest}
    >
      {children}
    </Ariakit.DialogDismiss>
  )
})

/* A button that can be leveraged within the `InformationalDialog.Controls` container. */
export const Button = forwardRef<HTMLButtonElement, ComponentProps<typeof LMFButton>>(
  function Button({ children, ...rest }, ref) {
    return (
      <LMFButton ref={ref} size='large' variant='primary' type='button' {...rest}>
        {children}
      </LMFButton>
    )
  }
)
